<!-- src/components/EditMealModal.vue -->
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_RECIPES, GET_MEAL_TYPES } from '../graphql/queries'
import { UPDATE_MEAL } from '../graphql/mutations'
import { showSuccess, showError } from '../utils/errorHandling'
import UnsavedChangesDialog from './UnsavedChangesDialog.vue'
import { useBodyScrollLock } from '../composables/useBodyScrollLock'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { type Meal } from '../graphql/types'

dayjs.extend(utc)

interface Props {
  show: boolean
  meal: Meal | null
}

const props = defineProps<Props>()
useBodyScrollLock(() => props.show)
const emit = defineEmits<{
  close: []
  updated: []
}>()

const currentStep = ref(1)

const searchQuery = ref('')
const debouncedSearch = ref('')
const currentPage = ref(1)
const PAGE_SIZE = 5
const selectedRecipe = ref<any>(null)

let searchTimer: ReturnType<typeof setTimeout>
watch(searchQuery, (val) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    debouncedSearch.value = val
    currentPage.value = 1
  }, 300)
})

const calendarMonth = ref(dayjs().startOf('month'))
const selectedDate = ref<dayjs.Dayjs | null>(null)
const originalScheduledDates = ref<string[]>([])
const selectedTypeId = ref('')
const timeInput = ref('')
// Tracks whether the time was set by the user (or carried from the existing meal),
// so auto-fill from the meal type never overwrites it.
const timeManuallySet = ref(false)
const showUnsavedChangesDialog = ref(false)

const { result: recipesResult, loading: loadingRecipes } = useQuery(
  GET_RECIPES,
  computed(() => ({ limit: 100, page: 0, search: debouncedSearch.value || undefined })),
  { fetchPolicy: 'cache-and-network' }
)

const { result: mealTypesResult } = useQuery(GET_MEAL_TYPES)

const { mutate: updateMeal, loading: mutationLoading } = useMutation(UPDATE_MEAL, {
  update(cache, { data: { updateMeal: updated } }) {
    cache.modify({
      id: cache.identify(updated),
      fields: {
        title() { return updated.title },
        description() { return updated.description },
        recipe_id() { return updated.recipe_id },
        type_id() { return updated.type_id },
        scheduled_date() { return updated.scheduled_date },
        updated_at() { return updated.updated_at }
      }
    })
  }
})

const allRecipes = computed(() => recipesResult.value?.recipes || [])
const mealTypes = computed(() => mealTypesResult.value?.mealTypes || [])

const pagedRecipes = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE
  return allRecipes.value.slice(start, start + PAGE_SIZE)
})

const totalPages = computed(() => Math.max(1, Math.ceil(allRecipes.value.length / PAGE_SIZE)))
const selectedTypeName = computed(() => mealTypes.value.find((m: any) => m.id === selectedTypeId.value)?.name || '')

// Format a meal type's "HH:MM" (24h) time into the modal's "h:mm A" display format.
const formatTypeTime = (t?: string | null): string => {
  if (!t) return ''
  const [hStr, mStr] = t.split(':')
  const h = Number(hStr)
  const m = Number(mStr)
  if (Number.isNaN(h) || Number.isNaN(m)) return ''
  const ampm = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

// Fill the Time from the chosen meal type, unless the user (or the existing meal)
// already provided one.
watch(selectedTypeId, (id) => {
  if (timeManuallySet.value) return
  const type = mealTypes.value.find((m: any) => m.id === id)
  const formatted = formatTypeTime(type?.time)
  if (formatted) timeInput.value = formatted
})

const canGoToStep2 = computed(() => selectedRecipe.value !== null)
const canGoToStep3 = computed(() => selectedDate.value !== null && selectedTypeId.value !== '')

const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const calendarDays = computed(() => {
  const start = calendarMonth.value
  const startDow = start.day()
  const startMon = (startDow + 6) % 7
  const daysInMonth = start.daysInMonth()
  const days: { date: dayjs.Dayjs; currentMonth: boolean }[] = []
  for (let i = startMon - 1; i >= 0; i--) {
    days.push({ date: start.subtract(i + 1, 'day'), currentMonth: false })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: start.date(d), currentMonth: true })
  }
  const remaining = 42 - days.length
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: start.add(daysInMonth - 1 + i, 'day'), currentMonth: false })
  }
  return days
})

const formattedSelectedDate = computed(() => {
  if (!selectedDate.value) return ''
  const d = selectedDate.value
  const day = d.date()
  const suffix =
    [1, 21, 31].includes(day) ? 'st' :
      [2, 22].includes(day) ? 'nd' :
        [3, 23].includes(day) ? 'rd' : 'th'
  return d.format(`dddd, [${day}${suffix} of] MMMM`)
})

const getInitials = (title: string) =>
  title.split(' ').filter(w => w && /^[A-ZÀ-Ú]/u.test(w)).map(w => w[0].toUpperCase()).slice(0, 3).join('')

const isSelectedDay = (date: dayjs.Dayjs) => selectedDate.value?.isSame(date, 'day') ?? false
const isToday = (date: dayjs.Dayjs) => dayjs().isSame(date, 'day')

const populateFromMeal = () => {
  if (!props.meal) return
  const recipe = allRecipes.value.find((r: any) => r.id === props.meal!.recipe_id)
  selectedRecipe.value = recipe ?? {
    id: props.meal.recipe_id,
    title: props.meal.title,
    description: props.meal.description || '',
  }
  timeManuallySet.value = false
  selectedTypeId.value = props.meal.type_id || ''
  originalScheduledDates.value = props.meal.scheduled_dates?.length
    ? props.meal.scheduled_dates
    : props.meal.scheduled_date ? [props.meal.scheduled_date] : []
  if (props.meal.scheduled_date) {
    const d = dayjs(props.meal.scheduled_date)
    calendarMonth.value = d.startOf('month')
    selectedDate.value = d
    const h = d.hour(), m = d.minute()
    if (h !== 0 || m !== 0) {
      const h12 = h % 12 || 12
      timeInput.value = `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
      // The meal already has a real time — protect it from meal-type auto-fill.
      timeManuallySet.value = true
    }
  }
  // Skip recipe selection — jump straight to date/time
  currentStep.value = 2
}

const resetForm = () => {
  currentStep.value = 1
  searchQuery.value = ''
  debouncedSearch.value = ''
  currentPage.value = 1
  selectedRecipe.value = null
  calendarMonth.value = dayjs().startOf('month')
  selectedDate.value = null
  originalScheduledDates.value = []
  selectedTypeId.value = ''
  timeInput.value = ''
  timeManuallySet.value = false
}

const closeModal = () => {
  resetForm()
  showUnsavedChangesDialog.value = false
  emit('close')
}

const attemptClose = () => { showUnsavedChangesDialog.value = true }
const nextStep = () => {
  if (currentStep.value === 1 && canGoToStep2.value) currentStep.value = 2
  else if (currentStep.value === 2 && canGoToStep3.value) currentStep.value = 3
}
const prevStep = () => { if (currentStep.value > 1) currentStep.value-- }

const submitMeal = async () => {
  try {
    let isoDate: string
    if (timeInput.value) {
      const parsed = dayjs(
        `${selectedDate.value!.format('YYYY-MM-DD')} ${timeInput.value}`,
        ['YYYY-MM-DD h:mm A', 'YYYY-MM-DD HH:mm']
      )
      isoDate = parsed.isValid() ? parsed.toISOString() : selectedDate.value!.startOf('day').toISOString()
    } else {
      isoDate = selectedDate.value!.startOf('day').toISOString()
    }

    // Shift every originally scheduled date by the same delta so multi-day meals
    // move as a block, mirroring MealsCalendarView's onDrop behavior.
    const originalPrimaryDate = props.meal!.scheduled_date
    const deltaDays = selectedDate.value!.startOf('day').diff(dayjs(originalPrimaryDate).startOf('day'), 'day')
    const newScheduledDates = originalScheduledDates.value.map((d) =>
      dayjs.utc(d).add(deltaDays, 'day').toISOString()
    )

    await updateMeal({
      id: props.meal!.id,
      title: selectedRecipe.value.title,
      description: selectedRecipe.value.description ?? null,
      recipe_id: selectedRecipe.value.id,
      type_id: selectedTypeId.value,
      scheduled_date: isoDate,
      scheduled_dates: newScheduledDates,
      portions_days: newScheduledDates.length || props.meal!.portions_days,
      portions_mode: props.meal!.portions_mode ?? undefined,
    })
    showSuccess('Meal updated successfully!')
    emit('updated')
    closeModal()
  } catch (e) {
    showError(e, 'update meal')
  }
}

watch(() => props.show, (show) => {
  if (show && props.meal) {
    resetForm()
    if (allRecipes.value.length > 0) populateFromMeal()
  } else if (!show) {
    resetForm()
  }
})

watch(allRecipes, (recipes) => {
  if (props.show && props.meal && recipes.length > 0 && !selectedRecipe.value) {
    populateFromMeal()
  }
})
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="attemptClose">
    <div class="bg-white rounded-lg w-full max-w-[720px] flex flex-col overflow-hidden modal-panel"
      role="dialog" aria-modal="true" aria-labelledby="edit-meal-title">

      <!-- ── Header ──────────────────────────────────────────────────────── -->
      <div class="flex-shrink-0 flex items-center justify-between px-6 modal-hd">
        <h2 id="edit-meal-title" class="font-bold text-[#0F172A] modal-title">Edit Meal</h2>
        <button @click="attemptClose" class="modal-close-button" aria-label="Close modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- ── Step Indicator ──────────────────────────────────────────────── -->
      <div class="flex-shrink-0 px-6 pt-6 step-bar">
        <div class="flex items-stretch gap-4">
          <div class="flex items-center gap-2 pb-3 border-b-2 transition-all"
            :class="currentStep === 1 ? 'border-[#4F8646]' : 'border-transparent opacity-50'">
            <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              :class="currentStep >= 1 ? 'bg-[#4F8646] text-white' : 'bg-[#E2E8F0] text-[#475569]'">1</div>
            <span class="text-sm font-bold whitespace-nowrap"
              :class="currentStep === 1 ? 'text-[#4F8646]' : 'text-[#64748B]'">Select Recipe</span>
          </div>
          <div class="flex items-center gap-2 pb-3 border-b-2 transition-all"
            :class="currentStep === 2 ? 'border-[#4F8646]' : 'border-transparent opacity-50'">
            <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              :class="currentStep >= 2 ? 'bg-[#4F8646] text-white' : 'bg-[#E2E8F0] text-[#475569]'">2</div>
            <span class="text-sm font-bold whitespace-nowrap"
              :class="currentStep === 2 ? 'text-[#4F8646]' : 'text-[#64748B]'">Date &amp; Time</span>
          </div>
          <div class="flex items-center gap-2 pb-3 border-b-2 transition-all"
            :class="currentStep === 3 ? 'border-[#4F8646]' : 'border-transparent opacity-50'">
            <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              :class="currentStep >= 3 ? 'bg-[#4F8646] text-white' : 'bg-[#E2E8F0] text-[#475569]'">3</div>
            <span class="text-sm font-bold whitespace-nowrap"
              :class="currentStep === 3 ? 'text-[#4F8646]' : 'text-[#64748B]'">Confirm</span>
          </div>
        </div>
      </div>

      <!-- ── Body ────────────────────────────────────────────────────────── -->
      <div class="flex-1 overflow-y-auto min-h-0 p-6">

        <!-- ── STEP 1: Select Recipe ─────────────────────────────────────── -->
        <div v-if="currentStep === 1" class="flex flex-col h-full gap-6">
          <div class="relative flex-shrink-0">
            <svg class="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] search-icon"
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
            </svg>
            <input v-model="searchQuery" type="text" placeholder="Search your recipes (e.g. Pasta, Salad...)"
              class="w-full rounded-lg text-sm text-[#0F172A] placeholder-[#6B7280] outline-none focus:ring-2 focus:ring-[#4F8646] transition-all search-input" />
          </div>

          <div class="flex-1 flex flex-col gap-4 overflow-y-auto min-h-0">
            <div v-if="loadingRecipes && allRecipes.length === 0" class="text-center py-10 text-sm text-[#64748B]">
              Loading recipes...
            </div>
            <div v-else-if="pagedRecipes.length === 0" class="text-center py-10 text-sm text-[#64748B]">
              No recipes found
            </div>

            <button v-for="recipe in pagedRecipes" :key="recipe.id" @click="selectedRecipe = recipe"
              class="w-full flex items-center gap-4 rounded-lg text-left transition-all recipe-option"
              :class="{ 'recipe-option--selected': selectedRecipe?.id === recipe.id }">
              <div class="flex-shrink-0 rounded-lg flex items-center justify-center recipe-thumb"
                :class="{ 'recipe-thumb--selected': selectedRecipe?.id === recipe.id }">
                <span class="font-bold select-none recipe-initials"
                  :class="{ 'recipe-initials--selected': selectedRecipe?.id === recipe.id }">
                  {{ getInitials(recipe.title) }}
                </span>
              </div>
              <div class="flex-1 min-w-0 flex flex-col gap-1">
                <p class="font-bold text-[#0F172A] truncate recipe-name">{{ recipe.title }}</p>
                <p v-if="recipe.description" class="text-xs text-[#64748B] truncate">{{ recipe.description }}</p>
                <div class="flex flex-wrap gap-1.5 mt-0.5">
                  <span v-if="recipe.category"
                    class="inline-flex items-center text-white text-xs font-normal px-2 recipe-cat-badge">
                    {{ recipe.category.name }}
                  </span>
                </div>
              </div>
              <div class="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all recipe-check"
                :class="{ 'recipe-check--selected': selectedRecipe?.id === recipe.id }">
                <svg v-if="selectedRecipe?.id === recipe.id" class="w-3 h-3" fill="none" stroke="white"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </button>
          </div>

          <div class="flex-shrink-0 flex items-center justify-between">
            <button @click="currentPage--" :disabled="currentPage === 1"
              class="flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded-md page-btn">
              <svg class="page-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
            <span class="page-info">Page {{ currentPage }} / {{ totalPages }}</span>
            <button @click="currentPage++" :disabled="currentPage >= totalPages"
              class="flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed rounded-md page-btn">
              Next
              <svg class="page-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <!-- ── STEP 2: Date & Time ────────────────────────────────────────── -->
        <div v-else-if="currentStep === 2" class="flex flex-col gap-6">
          <div class="flex flex-col gap-3">
            <h3 class="font-bold text-[#0F172A] step-section-title">Select Date</h3>
            <div class="rounded-lg cal-wrap">
              <div class="flex items-center justify-between mb-4">
                <button @click="calendarMonth = calendarMonth.subtract(1, 'month')"
                  class="p-1.5 rounded-lg transition-colors hover:bg-slate-100 text-[#64748B]"
                  aria-label="Previous month">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span class="font-bold text-[#0F172A] step-section-title">
                  {{ calendarMonth.format('MMMM YYYY') }}
                </span>
                <button @click="calendarMonth = calendarMonth.add(1, 'month')"
                  class="p-1.5 rounded-lg transition-colors hover:bg-slate-100 text-[#64748B]"
                  aria-label="Next month">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <div class="grid grid-cols-7 mb-1">
                <div v-for="d in WEEK_DAYS" :key="d"
                  class="text-center font-bold text-[#94A3B8] cal-head-cell">{{ d }}</div>
              </div>
              <div class="grid grid-cols-7">
                <div v-for="day in calendarDays" :key="day.date.toISOString()"
                  class="flex items-center justify-center cal-cell">
                  <button @click="day.currentMonth ? (selectedDate = day.date) : undefined"
                    class="w-8 h-8 flex items-center justify-center text-sm transition-colors cal-day" :class="[
                      !day.currentMonth ? 'cursor-default' : 'cursor-pointer',
                      isSelectedDay(day.date) ? 'rounded-lg font-semibold cal-day--selected'
                        : isToday(day.date) ? 'rounded-lg font-medium cal-day--today'
                          : day.currentMonth ? 'rounded-md hover:bg-slate-100 cal-day--current'
                            : 'cal-day--other'
                    ]">
                    {{ day.date.date() }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <h3 class="font-bold text-[#0F172A] meal-details-title">Meal Details</h3>
            <div class="grid grid-cols-2 gap-6">
              <div class="flex flex-col gap-1.5">
                <label class="font-bold text-[#334155] form-label">Meal Type</label>
                <select v-model="selectedTypeId"
                  class="w-full rounded-lg text-[#0F172A] outline-none focus:ring-2 focus:ring-[#4F8646] transition-all bg-white appearance-none px-4 form-select">
                  <option value="" disabled>Select type</option>
                  <option v-for="type in mealTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                </select>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="font-bold text-[#334155] form-label">
                  Time <span class="font-normal text-[#64748B]">(Optional)</span>
                </label>
                <input v-model="timeInput" @input="timeManuallySet = true" type="text" placeholder="12:30 PM"
                  class="w-full rounded-lg text-[#0F172A] placeholder-[#94A3B8] outline-none focus:ring-2 focus:ring-[#4F8646] transition-all form-input" />
              </div>
            </div>
          </div>
        </div>

        <!-- ── STEP 3: Confirm ────────────────────────────────────────────── -->
        <div v-else-if="currentStep === 3" class="flex flex-col gap-6">
          <div class="flex flex-col items-center gap-3 text-center">
            <h3 class="font-bold text-[#0F172A] flex items-center gap-2 confirm-title">
              Looks good!
              <svg class="w-6 h-6 text-[#4F8646]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0" />
              </svg>
            </h3>
            <p class="text-[#475569] confirm-subtitle">Review your changes before saving.</p>
          </div>

          <div class="flex items-start gap-6 rounded-lg summary-card">
            <div class="flex-shrink-0 rounded-lg flex items-center justify-center summary-thumb">
              <span class="font-bold select-none summary-initials">
                {{ getInitials(selectedRecipe?.title ?? '') }}
              </span>
            </div>
            <div class="flex-1 grid grid-cols-2 gap-x-6 gap-y-4">
              <div class="flex flex-col gap-1">
                <p class="font-bold text-[#64748B] uppercase summary-label">Recipe</p>
                <p class="font-bold text-[#0F172A] summary-value">{{ selectedRecipe?.title }}</p>
              </div>
              <div class="flex flex-col gap-1">
                <p class="font-bold text-[#64748B] uppercase summary-label">Meal Type</p>
                <p class="font-bold text-[#0F172A] summary-value">{{ selectedTypeName }}</p>
              </div>
              <div class="flex flex-col gap-1">
                <p class="font-bold text-[#64748B] uppercase summary-label">Date</p>
                <p class="font-bold text-[#0F172A] summary-value">{{ formattedSelectedDate }}</p>
              </div>
              <div class="flex flex-col gap-1">
                <p class="font-bold text-[#64748B] uppercase summary-label">Recipe Type</p>
                <p class="font-bold text-[#0F172A] summary-value">{{ selectedRecipe?.category?.name || '—' }}</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- ── Footer ──────────────────────────────────────────────────────── -->
      <div class="flex-shrink-0 flex items-center justify-between modal-ft">
        <button v-if="currentStep === 1" @click="attemptClose"
          class="font-semibold transition-colors hover:opacity-80 cancel-btn">
          Cancel
        </button>
        <button v-else @click="prevStep"
          class="flex items-center gap-2 rounded-lg font-bold transition-colors hover:opacity-90 back-btn">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <button v-if="currentStep === 1" @click="nextStep" :disabled="!canGoToStep2"
          class="rounded-lg font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed primary-action-btn">
          Next: Date &amp; Time
        </button>
        <button v-else-if="currentStep === 2" @click="nextStep" :disabled="!canGoToStep3"
          class="rounded-lg font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed primary-action-btn">
          Next: Confirm
        </button>
        <button v-else @click="submitMeal" :disabled="mutationLoading"
          class="rounded-lg font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed primary-action-btn">
          {{ mutationLoading ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>

    </div>
  </div>

  <UnsavedChangesDialog :show="showUnsavedChangesDialog" @keep-editing="showUnsavedChangesDialog = false"
    @discard="closeModal" />
</template>

<style scoped>
/* ── Modal shell ── */
.modal-panel         { max-height: 90vh; box-shadow: 0px 25px 50px -12px rgba(0,0,0,.25); }
.modal-hd            { border-bottom: 1px solid #F1F5F9; min-height: 64px; }
.modal-title         { font-size: 20px; }
.step-bar            { border-bottom: 2px solid #F1F5F9; }

/* ── Search ── */
.search-icon         { width: 18px; height: 18px; }
.search-input        { background: #F1F5F9; padding: 14px 16px 14px 48px; border: none; }

/* ── Recipe option ── */
.recipe-option            { padding: 12px; background: transparent; border: 1px solid #F1F5F9; }
.recipe-option--selected  { background: rgba(79,134,70,.05); border-color: #4F8646; }
.recipe-thumb             { width: 80px; height: 80px; background: #E2E8F0; }
.recipe-thumb--selected   { background: rgba(79,134,70,.4); }
.recipe-initials          { font-size: 18px; letter-spacing: 0.02em; color: #94A3B8; }
.recipe-initials--selected{ color: #2d5a26; }
.recipe-name              { font-size: 16px; line-height: 1.5; }
.recipe-cat-badge         { background: #FF9800; border-radius: 20px; height: 22px; }
.recipe-check             { border-color: #E2E8F0; background: transparent; }
.recipe-check--selected   { border-color: #4F8646; background: #4F8646; }

/* ── Pagination ── */
.page-btn            { background: rgba(79,134,70,.2); padding: 8px; height: 29px; font-size: 12px; color: #000; }
.page-btn-icon       { width: 14px; height: 14px; }
.page-info           { font-size: 12px; color: #000; }

/* ── Calendar ── */
.step-section-title  { font-size: 16px; }
.cal-wrap            { background: rgba(248,250,252,.5); border: 1px solid #E2E8F0; padding: 16px; }
.cal-head-cell       { font-size: 11px; padding: 4px 0; }
.cal-cell            { height: 36px; }
.cal-day--selected   { background: #4F8646; color: white; }
.cal-day--today      { background: rgba(79,134,70,.1); color: #4F8646; }
.cal-day--other      { color: #CBD5E1; }
.cal-day--current    { color: #0F172A; }

/* ── Meal details form ── */
.meal-details-title  { font-size: 18px; letter-spacing: -0.015em; }
.form-label          { font-size: 14px; }
.form-select         { border: 1px solid #E2E8F0; height: 42px; font-size: 16px; }
.form-input          { border: 1px solid #E2E8F0; height: 42px; font-size: 16px; padding: 8px 16px; }

/* ── Confirm step ── */
.confirm-title       { font-size: 28px; }
.confirm-subtitle    { font-size: 16px; }
.summary-card        { background: #F8FAFC; padding: 24px; }
.summary-thumb       { width: 128px; height: 128px; background: #B4CBB0; box-shadow: 0px 1px 2px 0px rgba(0,0,0,.05); }
.summary-initials    { font-size: 28px; letter-spacing: 0.02em; color: #2d5a26; }
.summary-label       { font-size: 10px; letter-spacing: 0.05em; }
.summary-value       { font-size: 14px; }

/* ── Footer ── */
.modal-ft            { background: #F8FAFC; border-top: 1px solid #F1F5F9; padding: 24px; }
.cancel-btn          { font-size: 14px; color: #475569; padding: 10px 24px; }
.back-btn            { background: rgba(79,134,70,.2); color: #334155; padding: 10px 24px; font-size: 14px; }
.primary-action-btn  { background: #4F8646; padding: 10px 32px; font-size: 14px;
                       box-shadow: 0px 4px 6px -4px rgba(79,134,70,.3), 0px 10px 15px -3px rgba(79,134,70,.3); }
</style>
